
import { useState } from "react";
import { Membership } from "../../../utils/types";
import { MEMBERSHIP_TYPES } from "../MembershipTypeFilter";
import { PAYMENT_STATUS_FILTERS } from "../PaymentStatusFilter";
import { isWithinInterval, parseISO, startOfDay, endOfDay } from "date-fns";

export const useMemberFilters = (members: Membership[]) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>(MEMBERSHIP_TYPES.map(type => type.id));
  const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState<string[]>(["paid", "not_paid"]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Filtrer les membres selon le type d'adhésion, le statut de paiement, la plage de dates et le terme de recherche
  const filteredMembers = members.filter(member => {
    // Filtre par type d'adhésion
    const typeMatches = selectedTypes.includes(member.membership_type);
    
    // Filtre par statut de paiement
    const isPaid = member.payment_status === "paid";
    
    const paymentStatusMatches = 
      (selectedPaymentStatuses.includes("paid") && isPaid) ||
      (selectedPaymentStatuses.includes("not_paid") && !isPaid);
    
    // Filtre par plage de dates
    let dateMatches = true;
    if (startDate || endDate) {
      const memberCreatedAt = parseISO(member.created_at);
      
      if (startDate && endDate) {
        dateMatches = isWithinInterval(memberCreatedAt, {
          start: startOfDay(startDate),
          end: endOfDay(endDate)
        });
      } else if (startDate) {
        dateMatches = memberCreatedAt >= startOfDay(startDate);
      } else if (endDate) {
        dateMatches = memberCreatedAt <= endOfDay(endDate);
      }
    }
    
    // Filtre par recherche (nom, prénom ou email)
    let searchMatches = true;
    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      searchMatches = 
        member.first_name.toLowerCase().includes(searchLower) || 
        member.last_name.toLowerCase().includes(searchLower) || 
        member.email.toLowerCase().includes(searchLower);
    }
    
    return typeMatches && paymentStatusMatches && dateMatches && searchMatches;
  });

  const handleTypeChange = (typeId: string) => {
    setSelectedTypes(prev => {
      if (prev.includes(typeId)) {
        // If removing the last type, keep it selected
        if (prev.length === 1) return prev;
        return prev.filter(t => t !== typeId);
      }
      return [...prev, typeId];
    });
  };

  const handlePaymentStatusChange = (statusId: string) => {
    setSelectedPaymentStatuses(prev => {
      if (prev.includes(statusId)) {
        // If removing the last status, keep it selected
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== statusId);
      }
      return [...prev, statusId];
    });
  };

  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
  };
  
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  return {
    selectedTypes,
    selectedPaymentStatuses,
    startDate,
    endDate,
    searchTerm,
    filteredMembers,
    handleTypeChange,
    handlePaymentStatusChange,
    handleStartDateChange,
    handleEndDateChange,
    handleSearchChange
  };
};
